import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Instagram, Facebook, Youtube, Linkedin, 
  RefreshCw, Check, X, Edit, Clock, Send,
  Image, Film, Play, Calendar, Settings,
  ChevronRight, Eye, Sparkles, AlertCircle, Video,
  Upload, Trash2, MessageSquare, CheckCircle
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PLATFORM_ICONS = {
  instagram: Instagram,
  facebook: Facebook,
  youtube: Youtube,
  linkedin: Linkedin
};

const PLATFORM_COLORS = {
  instagram: 'bg-gradient-to-r from-purple-500 to-pink-500',
  facebook: 'bg-blue-600',
  youtube: 'bg-red-600',
  linkedin: 'bg-blue-700'
};

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  editing: 'bg-blue-100 text-blue-800',
  scheduled: 'bg-purple-100 text-purple-800',
  posted: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-red-100 text-red-800'
};

const CONTENT_TYPE_ICONS = {
  carousel: Image,
  story: Play,
  reel: Film
};

export default function SocialMediaDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [content, setContent] = useState([]);
  const [selectedContent, setSelectedContent] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [filter, setFilter] = useState({ status: '', type: '' });
  const [youtubeStatus, setYoutubeStatus] = useState({ connected: false, channel: null });
  const [connectingYoutube, setConnectingYoutube] = useState(false);
  const [uploadingToYoutube, setUploadingToYoutube] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchDashboard();
    fetchContent();
    fetchYoutubeStatus();
    
    // Check for YouTube callback params
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('youtube_connected') === 'true') {
      toast.success('YouTube connected successfully!');
      fetchYoutubeStatus();
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (urlParams.get('youtube_error')) {
      toast.error('Failed to connect YouTube. Please try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/social-media/dashboard`, getAuthHeaders());
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      if (error.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchYoutubeStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/youtube/status`, getAuthHeaders());
      setYoutubeStatus(response.data);
    } catch (error) {
      console.error('Error fetching YouTube status:', error);
    }
  };

  const connectYoutube = async () => {
    setConnectingYoutube(true);
    try {
      const response = await axios.get(`${API_URL}/youtube/auth-url`, getAuthHeaders());
      // Redirect to Google OAuth
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Failed to initiate YouTube connection');
      setConnectingYoutube(false);
    }
  };

  const disconnectYoutube = async () => {
    try {
      await axios.post(`${API_URL}/youtube/disconnect`, {}, getAuthHeaders());
      setYoutubeStatus({ connected: false, channel: null });
      toast.success('YouTube disconnected');
    } catch (error) {
      toast.error('Failed to disconnect YouTube');
    }
  };

  const uploadToYoutube = async (contentId) => {
    setUploadingToYoutube(true);
    try {
      const response = await axios.post(
        `${API_URL}/youtube/upload?content_id=${contentId}`,
        {},
        getAuthHeaders()
      );
      if (response.data.success) {
        toast.success('Video uploaded to YouTube!');
        fetchContent();
        return response.data;
      } else {
        toast.error(response.data.error || 'Upload failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload to YouTube');
    } finally {
      setUploadingToYoutube(false);
    }
  };

  const fetchContent = async () => {
    try {
      let url = `${API_URL}/admin/social-media/content?page=1&page_size=50`;
      if (filter.status) url += `&status=${filter.status}`;
      if (filter.type) url += `&content_type=${filter.type}`;
      
      const response = await axios.get(url, getAuthHeaders());
      setContent(response.data.content || []);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const generateDailyContent = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/social-media/generate-daily-content`,
        {},
        getAuthHeaders()
      );
      toast.success(`Generated ${response.data.total_items} content items!`);
      fetchDashboard();
      fetchContent();
    } catch (error) {
      toast.error('Failed to generate content');
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  const approveContent = async (contentId) => {
    try {
      await axios.post(`${API_URL}/admin/social-media/content/${contentId}/approve`, {}, getAuthHeaders());
      toast.success('Content approved!');
      fetchContent();
    } catch (error) {
      toast.error('Failed to approve content');
    }
  };

  const rejectContent = async (contentId, remarks = '') => {
    try {
      await axios.post(`${API_URL}/admin/social-media/content/${contentId}/reject?remarks=${encodeURIComponent(remarks)}`, {}, getAuthHeaders());
      toast.success('Content rejected and removed');
      fetchContent();
    } catch (error) {
      toast.error('Failed to reject content');
    }
  };

  const deleteContent = async (contentId) => {
    try {
      await axios.delete(`${API_URL}/admin/social-media/content/${contentId}`, getAuthHeaders());
      toast.success('Content deleted');
      fetchContent();
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const updateContentWithRemarks = async (contentId, remarks, referenceUrl) => {
    try {
      await axios.put(
        `${API_URL}/admin/social-media/content/${contentId}/edit`,
        { remarks, reference_url: referenceUrl },
        getAuthHeaders()
      );
      toast.success('Content updated with remarks');
      fetchContent();
    } catch (error) {
      toast.error('Failed to update content');
    }
  };

  const scheduleAllApproved = async () => {
    try {
      const response = await axios.post(`${API_URL}/admin/social-media/schedule-approved`, {}, getAuthHeaders());
      toast.success(response.data.message);
      fetchContent();
    } catch (error) {
      toast.error('Failed to schedule content');
    }
  };

  const postNow = async (contentId, platforms) => {
    try {
      const response = await axios.post(
        `${API_URL}/admin/social-media/content/${contentId}/post-now`,
        platforms,
        getAuthHeaders()
      );
      toast.success('Content posted!');
      setPreviewOpen(false);
      fetchContent();
    } catch (error) {
      toast.error('Failed to post content');
    }
  };

  const batchApprove = async () => {
    const pendingIds = content.filter(c => c.status === 'pending').map(c => c.id);
    if (pendingIds.length === 0) {
      toast.info('No pending content to approve');
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/social-media/batch-approve`, pendingIds, getAuthHeaders());
      toast.success(`Approved ${pendingIds.length} items`);
      fetchContent();
    } catch (error) {
      toast.error('Failed to batch approve');
    }
  };

  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [generatingAllVideos, setGeneratingAllVideos] = useState(false);

  const generateVideoForContent = async (contentId) => {
    setGeneratingVideo(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/social-media/content/${contentId}/generate-video`,
        {},
        getAuthHeaders()
      );
      toast.success(`Video generated! ${response.data.file_size_mb}MB, ${response.data.duration}s`);
      fetchContent();
      setPreviewOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate video');
    } finally {
      setGeneratingVideo(false);
    }
  };

  const generateAllReelVideos = async () => {
    setGeneratingAllVideos(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/social-media/generate-all-videos`,
        {},
        getAuthHeaders()
      );
      toast.success(response.data.message);
      fetchContent();
    } catch (error) {
      toast.error('Failed to generate videos');
    } finally {
      setGeneratingAllVideos(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black uppercase mb-2">Social Media Automation</h1>
            <p className="text-gray-600">Generate, review, and publish content to all platforms</p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <Button 
              onClick={generateDailyContent} 
              disabled={generating}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              {generating ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Generate Daily Content
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending Review</p>
                  <p className="text-3xl font-bold text-yellow-600">{dashboard?.statistics?.pending_review || 0}</p>
                </div>
                <Clock className="w-10 h-10 text-yellow-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Scheduled</p>
                  <p className="text-3xl font-bold text-purple-600">{dashboard?.statistics?.scheduled || 0}</p>
                </div>
                <Calendar className="w-10 h-10 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Posted</p>
                  <p className="text-3xl font-bold text-green-600">{dashboard?.statistics?.total_posted || 0}</p>
                </div>
                <Check className="w-10 h-10 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Platforms</p>
                  <p className="text-3xl font-bold text-blue-600">4</p>
                </div>
                <Send className="w-10 h-10 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="content">Content Library</TabsTrigger>
            <TabsTrigger value="posted">Posted Content</TabsTrigger>
            <TabsTrigger value="platforms">Platforms</TabsTrigger>
            <TabsTrigger value="schedule">Schedule</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Pending Review */}
              <Card className="lg:col-span-2">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Pending Review</CardTitle>
                    <CardDescription>Content waiting for your approval</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={batchApprove}>
                      <Check className="w-4 h-4 mr-1" /> Approve All
                    </Button>
                    <Button size="sm" onClick={scheduleAllApproved}>
                      <Calendar className="w-4 h-4 mr-1" /> Schedule Approved
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {content.filter(c => c.status === 'pending').slice(0, 10).map((item) => (
                      <ContentCard 
                        key={item.id} 
                        item={item} 
                        onPreview={() => { setSelectedContent(item); setPreviewOpen(true); }}
                        onApprove={() => approveContent(item.id)}
                        onReject={() => rejectContent(item.id)}
                        onGenerateVideo={() => generateVideoForContent(item.id)}
                        generatingVideo={generatingVideo}
                      />
                    ))}
                    {content.filter(c => c.status === 'pending').length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No pending content. Generate new content to get started!</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Platform Status */}
              <Card>
                <CardHeader>
                  <CardTitle>Platform Status</CardTitle>
                  <CardDescription>Connection status for each platform</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {['instagram', 'facebook', 'youtube', 'linkedin'].map((platform) => {
                      const cred = dashboard?.platform_credentials?.find(c => c.platform === platform);
                      const Icon = PLATFORM_ICONS[platform];
                      
                      // Special handling for YouTube
                      const isYoutubeConnected = platform === 'youtube' && youtubeStatus.connected;
                      const isConfigured = platform === 'youtube' ? isYoutubeConnected : cred?.is_configured;
                      
                      return (
                        <div key={platform} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg ${PLATFORM_COLORS[platform]} flex items-center justify-center`}>
                              <Icon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <span className="font-medium capitalize">{platform}</span>
                              {platform === 'youtube' && isYoutubeConnected && youtubeStatus.channel && (
                                <p className="text-xs text-gray-500">{youtubeStatus.channel.title}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {platform === 'youtube' && !isYoutubeConnected && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={connectYoutube}
                                disabled={connectingYoutube}
                                className="text-red-600 border-red-300 hover:bg-red-50"
                              >
                                {connectingYoutube ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Connect'}
                              </Button>
                            )}
                            {platform === 'youtube' && isYoutubeConnected && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={disconnectYoutube}
                                className="text-gray-500 hover:text-red-600"
                              >
                                Disconnect
                              </Button>
                            )}
                            <Badge variant={isConfigured ? "default" : "secondary"}>
                              {isConfigured ? 'Connected' : 'Not Connected'}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Button variant="outline" className="w-full mt-4" onClick={() => setActiveTab('platforms')}>
                    <Settings className="w-4 h-4 mr-2" /> Configure Platforms
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Content Library Tab - Show only pending/unreviewed content */}
          <TabsContent value="content">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <CardTitle>Content Library</CardTitle>
                    <CardDescription>Pending content for review (rejected content is auto-deleted)</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <select 
                      className="border rounded-md px-3 py-2 text-sm"
                      value={filter.status}
                      onChange={(e) => { setFilter({...filter, status: e.target.value}); setTimeout(fetchContent, 100); }}
                    >
                      <option value="">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="scheduled">Scheduled</option>
                    </select>
                    <select 
                      className="border rounded-md px-3 py-2 text-sm"
                      value={filter.type}
                      onChange={(e) => { setFilter({...filter, type: e.target.value}); setTimeout(fetchContent, 100); }}
                    >
                      <option value="">All Types</option>
                      <option value="carousel">Carousel</option>
                      <option value="story">Story</option>
                    </select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {content.filter(c => c.status !== 'posted' && c.status !== 'rejected').map((item) => (
                    <ContentCard 
                      key={item.id} 
                      item={item} 
                      detailed
                      onPreview={() => { setSelectedContent(item); setPreviewOpen(true); }}
                      onApprove={() => approveContent(item.id)}
                      onReject={() => rejectContent(item.id)}
                      onDelete={() => deleteContent(item.id)}
                      onGenerateVideo={() => generateVideoForContent(item.id)}
                      generatingVideo={generatingVideo}
                    />
                  ))}
                </div>
                {content.filter(c => c.status !== 'posted' && c.status !== 'rejected').length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <Image className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>No pending content. Generate daily content to get started!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Posted Content Tab */}
          <TabsContent value="posted">
            <Card>
              <CardHeader>
                <CardTitle>Posted Content</CardTitle>
                <CardDescription>Content that has been approved, scheduled, or posted to social media</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {content.filter(c => c.status === 'posted' || c.status === 'scheduled' || c.youtube_url).map((item) => (
                    <div key={item.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border">
                      {/* Thumbnail */}
                      <div className="w-20 h-20 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                        {item.video_url ? (
                          <video src={`${process.env.REACT_APP_BACKEND_URL}${item.video_url}`} className="w-full h-full object-cover" muted />
                        ) : item.media_urls?.[0] ? (
                          <img src={item.media_urls[0]} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <Image className="w-8 h-8" />
                          </div>
                        )}
                      </div>
                      
                      {/* Content Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium capitalize">{item.content_type}</span>
                          <Badge className={STATUS_COLORS[item.status]}>{item.status}</Badge>
                        </div>
                        <p className="text-sm text-gray-600 truncate">{item.product_name}</p>
                        <p className="text-xs text-gray-400 truncate">{item.caption?.substring(0, 60)}...</p>
                      </div>
                      
                      {/* Posted Details */}
                      <div className="text-right flex-shrink-0">
                        {item.youtube_url && (
                          <a href={item.youtube_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-red-600 text-sm hover:underline mb-1">
                            <Youtube className="w-4 h-4" /> YouTube
                          </a>
                        )}
                        {item.youtube_posted_at && (
                          <p className="text-xs text-gray-500">
                            Posted: {new Date(item.youtube_posted_at).toLocaleDateString()} {new Date(item.youtube_posted_at).toLocaleTimeString()}
                          </p>
                        )}
                        {item.scheduled_time && (
                          <p className="text-xs text-purple-600">
                            Scheduled: {new Date(item.scheduled_time).toLocaleDateString()} {new Date(item.scheduled_time).toLocaleTimeString()}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                  {content.filter(c => c.status === 'posted' || c.status === 'scheduled' || c.youtube_url).length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <CheckCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p>No posted content yet. Approve and post content to see it here!</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Platforms Tab */}
          <TabsContent value="platforms">
            <PlatformsConfig 
              credentials={dashboard?.platform_credentials} 
              onUpdate={fetchDashboard} 
              youtubeStatus={youtubeStatus}
              onConnectYoutube={connectYoutube}
              onDisconnectYoutube={disconnectYoutube}
              connectingYoutube={connectingYoutube}
            />
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule">
            <ScheduleConfig />
          </TabsContent>
        </Tabs>
      </div>

      {/* Content Preview Dialog */}
      <ContentPreviewDialog 
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        content={selectedContent}
        onApprove={() => { approveContent(selectedContent?.id); setPreviewOpen(false); }}
        onReject={(remarks) => { rejectContent(selectedContent?.id, remarks); setPreviewOpen(false); }}
        onPost={(platforms) => postNow(selectedContent?.id, platforms)}
        onGenerateVideo={() => generateVideoForContent(selectedContent?.id)}
        generatingVideo={generatingVideo}
        youtubeConnected={youtubeStatus.connected}
        onUploadToYoutube={uploadToYoutube}
        uploadingToYoutube={uploadingToYoutube}
        onSaveRemarks={updateContentWithRemarks}
      />
    </div>
  );
}

// Content Card Component
function ContentCard({ item, detailed = false, onPreview, onApprove, onReject, onDelete, onGenerateVideo, generatingVideo }) {
  const TypeIcon = CONTENT_TYPE_ICONS[item.content_type] || Image;
  const isReel = item.content_type === 'reel';
  const hasVideo = item.video_url;
  const videoGenerating = item.video_status === 'generating';
  
  return (
    <div className="border rounded-lg p-4 bg-white hover:shadow-md transition-shadow flex flex-col">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
            <TypeIcon className="w-4 h-4 text-gray-600" />
          </div>
          <div>
            <p className="font-medium capitalize text-sm">{item.content_type}</p>
            <p className="text-xs text-gray-500">{item.product_name}</p>
          </div>
        </div>
        <div className="flex flex-col gap-1 items-end">
          <Badge className={STATUS_COLORS[item.status]}>{item.status}</Badge>
        </div>
      </div>
      
      {/* Media Preview */}
      {item.media_urls?.[0] && (
        <div className="mb-3 rounded-lg overflow-hidden bg-gray-100 h-32 flex-shrink-0">
          <img 
            src={item.media_urls[0]} 
            alt={item.product_name}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      
      {/* Caption - Show full text for review */}
      <div className="flex-1 mb-3 max-h-48 overflow-y-auto">
        <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
          {item.caption}
        </p>
      </div>
      
      {/* Hashtags preview */}
      {item.hashtags && item.hashtags.length > 0 && (
        <p className="text-xs text-blue-600 mb-3 truncate">
          {item.hashtags.slice(0, 5).join(' ')}...
        </p>
      )}
      
      <div className="flex gap-2 flex-wrap mt-auto">
        <Button size="sm" variant="outline" onClick={onPreview} className="flex-1">
          <Eye className="w-3 h-3 mr-1" /> Preview
        </Button>
        {item.status === 'pending' && (
          <>
            <Button size="sm" variant="default" onClick={onApprove} className="bg-green-600 hover:bg-green-700">
              <Check className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="destructive" onClick={onReject}>
              <X className="w-3 h-3" />
            </Button>
          </>
        )}
        {onDelete && (
          <Button size="sm" variant="ghost" onClick={onDelete} className="text-gray-400 hover:text-red-600">
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

// Content Preview Dialog
function ContentPreviewDialog({ open, onOpenChange, content, onApprove, onReject, onPost, onGenerateVideo, generatingVideo, youtubeConnected, onUploadToYoutube, uploadingToYoutube, onSaveRemarks }) {
  const [remarks, setRemarks] = useState('');
  const [referenceUrl, setReferenceUrl] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState(['instagram', 'facebook', 'linkedin', 'youtube']);
  
  if (!content) return null;
  
  const isReel = content.content_type === 'reel';
  const hasVideo = content.video_url;
  const alreadyPostedToYoutube = content.youtube_url;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="capitalize">{content.content_type} Preview</DialogTitle>
          <DialogDescription>Review and approve this content for publishing</DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Media Preview */}
          <div>
            {/* Show video for reels if available */}
            {isReel && hasVideo ? (
              <div className="rounded-lg overflow-hidden bg-black mb-4">
                <video 
                  src={`${process.env.REACT_APP_BACKEND_URL}${content.video_url}`}
                  className="w-full"
                  controls
                  playsInline
                  poster={content.media_urls?.[0]}
                  onError={(e) => console.log('Video load error')}
                >
                  Your browser does not support the video tag.
                </video>
                <div className="bg-green-100 p-2 text-sm text-green-800 flex items-center gap-2">
                  <Video className="w-4 h-4" />
                  Video Ready: {content.video_duration || '30'}s | {content.video_file_size_mb || 'N/A'}MB | Music: {content.video_music || 'Background'}
                </div>
                {/* YouTube Upload Button */}
                {youtubeConnected && !alreadyPostedToYoutube && (
                  <div className="bg-red-50 p-3 border-t">
                    <Button 
                      size="sm"
                      onClick={() => onUploadToYoutube(content.id)}
                      disabled={uploadingToYoutube}
                      className="bg-red-600 hover:bg-red-700 w-full"
                    >
                      {uploadingToYoutube ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Uploading to YouTube...</>
                      ) : (
                        <><Youtube className="w-4 h-4 mr-2" /> Upload to YouTube Shorts</>
                      )}
                    </Button>
                  </div>
                )}
                {alreadyPostedToYoutube && (
                  <div className="bg-green-50 p-3 border-t">
                    <a 
                      href={content.youtube_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-green-700 flex items-center gap-2 hover:underline"
                    >
                      <Youtube className="w-4 h-4" />
                      View on YouTube
                    </a>
                  </div>
                )}
              </div>
            ) : content.media_urls?.[0] ? (
              <div className="rounded-lg overflow-hidden bg-gray-100 mb-4">
                <img 
                  src={content.media_urls[0]} 
                  alt={content.product_name}
                  className="w-full object-cover"
                />
                {isReel && !hasVideo && (
                  <div className="bg-yellow-100 p-3 text-sm text-yellow-800">
                    <p className="font-medium mb-2">No video generated yet for this reel.</p>
                    <Button 
                      size="sm" 
                      onClick={onGenerateVideo}
                      disabled={generatingVideo}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {generatingVideo ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                      ) : (
                        <><Video className="w-4 h-4 mr-2" /> Generate Video</>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            ) : null}
            
            <div className="space-y-2">
              <Badge className={STATUS_COLORS[content.status]}>{content.status}</Badge>
              <p className="text-sm text-gray-600"><strong>Product:</strong> {content.product_name}</p>
              <p className="text-sm text-gray-600"><strong>Category:</strong> {content.product_category}</p>
            </div>
          </div>
          
          {/* Content Details */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Caption</label>
              <div className="mt-1 p-3 bg-gray-50 rounded-lg text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">
                {content.caption}
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">Hashtags</label>
              <div className="mt-1 flex flex-wrap gap-1">
                {content.hashtags?.map((tag, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                ))}
              </div>
            </div>
            
            {content.youtube_title && (
              <div>
                <label className="text-sm font-medium">YouTube Title</label>
                <p className="mt-1 text-sm bg-gray-50 p-2 rounded">{content.youtube_title}</p>
              </div>
            )}
            
            {content.linkedin_caption && (
              <div>
                <label className="text-sm font-medium">LinkedIn Caption</label>
                <div className="mt-1 p-2 bg-gray-50 rounded text-sm max-h-32 overflow-y-auto">
                  {content.linkedin_caption}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Target Audience */}
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium mb-2">Target Audience</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-gray-500">Location</p>
              <p className="font-medium">{content.target_audience?.locations?.join(', ') || 'India'}</p>
            </div>
            <div>
              <p className="text-gray-500">Age Range</p>
              <p className="font-medium">{content.target_audience?.age_range || '25-55'}</p>
            </div>
            <div>
              <p className="text-gray-500">Industries</p>
              <p className="font-medium">{content.target_audience?.industries?.slice(0, 3).join(', ')}</p>
            </div>
            <div>
              <p className="text-gray-500">Job Titles</p>
              <p className="font-medium">{content.target_audience?.job_titles?.slice(0, 3).join(', ')}</p>
            </div>
          </div>
        </div>
        
        {/* Actions */}
        {content.status === 'pending' && (
          <div className="mt-6 space-y-4">
            {/* Remarks / Corrections Section */}
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Corrections / Remarks
              </h4>
              <Textarea 
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Add your corrections, feedback or desired changes..."
                className="mb-3"
                rows={3}
              />
              
              {/* Reference URL Upload */}
              <div className="mt-3">
                <Label className="text-sm text-gray-600">Reference Content URL (optional)</Label>
                <p className="text-xs text-gray-500 mb-2">Paste a link to similar content you'd like us to reference</p>
                <Input
                  value={referenceUrl}
                  onChange={(e) => setReferenceUrl(e.target.value)}
                  placeholder="https://instagram.com/p/... or YouTube link"
                  className="text-sm"
                />
              </div>
              
              {(remarks || referenceUrl) && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="mt-3 border-yellow-400 text-yellow-700"
                  onClick={() => onSaveRemarks && onSaveRemarks(content.id, remarks, referenceUrl)}
                >
                  <Edit className="w-3 h-3 mr-2" /> Save for Revision
                </Button>
              )}
            </div>
            
            <div className="flex gap-3">
              <Button onClick={onApprove} className="flex-1 bg-green-600 hover:bg-green-700">
                <Check className="w-4 h-4 mr-2" /> Approve
              </Button>
              <Button onClick={() => onReject(remarks)} variant="destructive" className="flex-1">
                <X className="w-4 h-4 mr-2" /> Reject & Delete
              </Button>
            </div>
          </div>
        )}
        
        {content.status === 'approved' && (
          <div className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Select Platforms to Post</label>
              <div className="flex flex-wrap gap-2">
                {['instagram', 'facebook', 'youtube', 'linkedin'].map((platform) => {
                  const Icon = PLATFORM_ICONS[platform];
                  const isSelected = selectedPlatforms.includes(platform);
                  return (
                    <Button
                      key={platform}
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        if (isSelected) {
                          setSelectedPlatforms(selectedPlatforms.filter(p => p !== platform));
                        } else {
                          setSelectedPlatforms([...selectedPlatforms, platform]);
                        }
                      }}
                      className={isSelected ? PLATFORM_COLORS[platform] : ''}
                    >
                      <Icon className="w-4 h-4 mr-1" />
                      {platform}
                    </Button>
                  );
                })}
              </div>
            </div>
            
            <Button 
              onClick={() => onPost(selectedPlatforms)} 
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
              disabled={selectedPlatforms.length === 0}
            >
              <Send className="w-4 h-4 mr-2" /> Post Now to {selectedPlatforms.length} Platform(s)
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Platforms Config Component
function PlatformsConfig({ credentials, onUpdate, youtubeStatus, onConnectYoutube, onDisconnectYoutube, connectingYoutube }) {
  const [saving, setSaving] = useState(false);
  
  const platforms = [
    {
      id: 'instagram',
      name: 'Instagram',
      icon: Instagram,
      color: 'from-purple-500 to-pink-500',
      instructions: [
        '1. Go to Meta Business Suite (business.facebook.com)',
        '2. Connect your Instagram Business Account',
        '3. Create an App in Meta for Developers',
        '4. Get your Access Token from Graph API Explorer',
        '5. Enter the Access Token below'
      ]
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: Facebook,
      color: 'from-blue-600 to-blue-700',
      instructions: [
        '1. Go to developers.facebook.com',
        '2. Create or select your App',
        '3. Add Facebook Login and Pages API products',
        '4. Generate a Page Access Token',
        '5. Enter the Access Token and Page ID below'
      ]
    },
    {
      id: 'youtube',
      name: 'YouTube',
      icon: Youtube,
      color: 'from-red-600 to-red-700',
      instructions: [
        '1. Go to Google Cloud Console',
        '2. Create a project and enable YouTube Data API v3',
        '3. Create OAuth 2.0 credentials',
        '4. Authorize with your YouTube channel',
        '5. Enter the Access Token and Channel ID below'
      ]
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: Linkedin,
      color: 'from-blue-700 to-blue-800',
      instructions: [
        '1. Go to linkedin.com/developers',
        '2. Create an App and verify your company page',
        '3. Request "Share on LinkedIn" product access',
        '4. Generate an Access Token',
        '5. Enter the Access Token below'
      ]
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {platforms.map((platform) => {
        const cred = credentials?.find(c => c.platform === platform.id);
        const Icon = platform.icon;
        
        // Special handling for YouTube - check actual connection status
        const isYoutubeConnected = platform.id === 'youtube' && youtubeStatus?.connected;
        const isConfigured = platform.id === 'youtube' ? isYoutubeConnected : cred?.is_configured;
        
        return (
          <Card key={platform.id}>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${platform.color} flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle>{platform.name}</CardTitle>
                  {platform.id === 'youtube' && isYoutubeConnected && youtubeStatus?.channel && (
                    <p className="text-xs text-gray-500">{youtubeStatus.channel.title}</p>
                  )}
                  <Badge variant={isConfigured ? "default" : "secondary"}>
                    {isConfigured ? 'Connected' : 'Not Connected'}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Show connected status for YouTube */}
                {platform.id === 'youtube' && isYoutubeConnected ? (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 text-green-700 mb-2">
                      <Check className="w-4 h-4" />
                      <span className="font-medium">YouTube Connected!</span>
                    </div>
                    <p className="text-sm text-green-600 mb-2">
                      Channel: {youtubeStatus?.channel?.title}
                    </p>
                    <p className="text-xs text-gray-500">
                      {youtubeStatus?.channel?.subscriber_count} subscribers | {youtubeStatus?.channel?.video_count} videos
                    </p>
                  </div>
                ) : (
                  <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5" />
                      <div className="text-sm text-yellow-800">
                        <p className="font-medium mb-1">Setup Instructions:</p>
                        <ol className="list-decimal list-inside space-y-1 text-xs">
                          {platform.instructions.map((step, i) => (
                            <li key={i}>{step.substring(3)}</li>
                          ))}
                        </ol>
                      </div>
                    </div>
                  </div>
                )}
                
                {cred?.is_configured && platform.id !== 'youtube' && (
                  <p className="text-sm text-green-600">
                    Last updated: {new Date(cred.updated_at).toLocaleDateString()}
                  </p>
                )}
                
                {/* Special button for YouTube */}
                {platform.id === 'youtube' ? (
                  isYoutubeConnected ? (
                    <Button 
                      variant="outline" 
                      className="w-full border-red-300 text-red-600 hover:bg-red-50"
                      onClick={onDisconnectYoutube}
                    >
                      Disconnect YouTube
                    </Button>
                  ) : (
                    <Button 
                      variant="default" 
                      className="w-full bg-red-600 hover:bg-red-700"
                      onClick={onConnectYoutube}
                      disabled={connectingYoutube}
                    >
                      {connectingYoutube ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Connecting...</>
                      ) : (
                        <>Connect YouTube <ChevronRight className="w-4 h-4 ml-2" /></>
                      )}
                    </Button>
                  )
                ) : (
                  <Button variant="outline" className="w-full">
                    {cred?.is_configured ? 'Update Credentials' : 'Configure'}
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// Schedule Config Component
function ScheduleConfig() {
  const [schedule, setSchedule] = useState({
    carousel_slots: [
      { hour: 9, minute: 0, platforms: ['instagram', 'facebook', 'linkedin'] },
      { hour: 13, minute: 0, platforms: ['instagram', 'facebook', 'linkedin'] },
      { hour: 18, minute: 0, platforms: ['instagram', 'facebook', 'linkedin'] }
    ],
    story_slots: [
      { hour: 10, minute: 0, platforms: ['instagram', 'facebook'] },
      { hour: 14, minute: 0, platforms: ['instagram', 'facebook'] },
      { hour: 19, minute: 0, platforms: ['instagram', 'facebook'] }
    ],
    reel_slots: [
      { hour: 11, minute: 0, platforms: ['instagram', 'facebook', 'youtube'] },
      { hour: 15, minute: 0, platforms: ['instagram', 'facebook', 'youtube'] },
      { hour: 20, minute: 0, platforms: ['instagram', 'facebook', 'youtube'] }
    ]
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Carousels */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="w-5 h-5" /> Carousel Posts
          </CardTitle>
          <CardDescription>3 posts per day</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {schedule.carousel_slots.map((slot, i) => (
              <div key={i} className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">
                  {String(slot.hour).padStart(2, '0')}:{String(slot.minute).padStart(2, '0')} IST
                </p>
                <div className="flex gap-1 mt-2">
                  {slot.platforms.map(p => {
                    const Icon = PLATFORM_ICONS[p];
                    return <Icon key={p} className="w-4 h-4 text-gray-500" />;
                  })}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Stories */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Play className="w-5 h-5" /> Stories
          </CardTitle>
          <CardDescription>3 stories per day</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {schedule.story_slots.map((slot, i) => (
              <div key={i} className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">
                  {String(slot.hour).padStart(2, '0')}:{String(slot.minute).padStart(2, '0')} IST
                </p>
                <div className="flex gap-1 mt-2">
                  {slot.platforms.map(p => {
                    const Icon = PLATFORM_ICONS[p];
                    return <Icon key={p} className="w-4 h-4 text-gray-500" />;
                  })}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Reels */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Film className="w-5 h-5" /> Reels / Shorts
          </CardTitle>
          <CardDescription>3 reels per day</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {schedule.reel_slots.map((slot, i) => (
              <div key={i} className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">
                  {String(slot.hour).padStart(2, '0')}:{String(slot.minute).padStart(2, '0')} IST
                </p>
                <div className="flex gap-1 mt-2">
                  {slot.platforms.map(p => {
                    const Icon = PLATFORM_ICONS[p];
                    return <Icon key={p} className="w-4 h-4 text-gray-500" />;
                  })}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
